import _ from 'lodash';

/**
 * Sit on top of $http and provide simplified access to API
 */
export function ApiProvider() {
	return {
		$get($http, $injector) {
			'ngInject';
			return new Api($http, $injector);
		},
	};
}

/**
 * The actual class available as Api in injections
 */
class Api {
	/**
	 * Note that vars are injected from above,
	 * not from ngInject
	 * @param {Object} $http
	 * @param {$injector} $injector
	 */
	constructor($http, $injector) {
		/**
		 * The angular $http service
		 */
		this.$http = $http;
		/**
		 * Our $injector service
		 */
		this.$injector = $injector;
		/**
		 * A collection of requests that are currently pending. Used to abort requests.
		 */
		this.pendingRequests = [];
		/**
		 * The last create response
		 */
		this.lastCreateResponse = null;
	}

	/**
	 * Make a GET request to the specified endpoint
	 * @param {String} endpoint  The endpoint such as "/posts/search"
	 * @param {Object} [params]  Parameters to send in the query string
	 * @param {Object} [headers] Additional headers to send
	 * @returns {Promise}
	 */
	get(endpoint, params, headers) {
		return this._getAbortableDataPromise(this.request('get', endpoint, params, headers));
	}

	/**
	 * Make a POST request to the specified endpoint
	 * @param {String} endpoint  The endpoint such as "/posts"
	 * @param {Object} [params]  Data to send in the post body
	 * @param {Object} [headers] Additional headers to send
	 * @returns {Promise}
	 */
	post(endpoint, params, headers) {
		return this._getAbortableDataPromise(this.request('post', endpoint, params, headers));
	}

	/**
	 * Make a PUT request to the specified endpoint
	 * @param {String} endpoint  The endpoint such as "/posts/:id/keywords"
	 * @param {Object} [params]  Data to send in the post body
	 * @param {Object} [headers] Additional headers to send
	 * @returns {Promise}
	 */
	put(endpoint, params, headers) {
		return this._getAbortableDataPromise(this.request('put', endpoint, params, headers));
	}

	/**
	 * Make a PATCH request to the specified endpoint
	 * @param {String} endpoint  The endpoint such as "/posts"
	 * @param {Object} [params]  Parameters to send in the patch payload
	 * @param {Object} [headers] Additional headers to send
	 * @returns {Promise}
	 */
	patch(endpoint, params, headers) {
		return this._getAbortableDataPromise(this.request('patch', endpoint, params, headers));
	}

	/**
	 * Make a DELETE request to the specified endpoint
	 * @param {String} endpoint  The endpoint such as "/posts/search"
	 * @param {Object} [params]  Parameters to send in the delete payload
	 * @param {Object} [headers] Additional headers to send
	 * @returns {Promise}
	 */
	delete(endpoint, params, headers) {
		return this._getAbortableDataPromise(this.request('delete', endpoint, params, headers));
	}

	/**
	 * Make a api request to the specified endpoint as a job.
	 * @param {String} verb  The http verb such as post, delete, patch
	 * @param {String} endpoint  The endpoint such as "/posts/search"
	 * @param {Object} [params]  Parameters to send in the delete payload
	 * @param {Object} [headers] Additional headers to send
	 * @returns {Promise}
	 */
	submitJob(verb, endpoint, params, headers) {
		if (!headers) {
			headers = {};
		}
		headers['Submit-As-Job'] = true;
		const promise = this.request(verb, endpoint, params, headers);
		return promise.then(
			response => {
				if (response.status === 202) {
					const jobId = response.data.job_id;
					response.onJobComplete = (callback, interval = 5000) => {
						let timer = setInterval(() => {
							this.get(`/api_jobs/${jobId}`)
								.then(data => {
									if (data.completed_at) {
										clearInterval(timer);
										callback(data);
									}
								})
								.catch(error => {
									this.Logger.write('SUBMIT_JOB_DEBUG', `Error getting job data for job ${jobId}`, error);
								});
						}, interval);
					};
				}
				return response;
			},
			response => response
		);
	}

	/**
	 * Make a request of the specified type to the specified endpoint
	 * @param {String} verb  The HTTP verb, one of get, post, patch, delete
	 * @param {String} endpoint  The endpoint such as "/posts/search"
	 * @param {Object} [params]  Parameters to send in the query string or payload
	 * @param {Object} [headers] Additional headers to send
	 * @returns {Promise}
	 */
	request(verb, endpoint, params, headers) {
		if (!endpoint) {
			// avoid requests to /api/v2null and /api/v2
			return Promise.resolve();
		}
		verb = verb.toLowerCase();
		var request = {
			method: verb,
			url: this._getPrefix(endpoint) + endpoint,
			headers: headers || {},
		};
		if (request.headers.avoidLoadingBar) {
			request.ignoreLoadingBar = true;
			request.headers.avoidLoadingBar = undefined;
		}
		if (verb == 'get' && params) {
			request.params = params;
		} else if (params) {
			request.data = params;
		}
		try {
			this.$injector.get('EventTracker').add('xhr', {
				method: verb,
				url: request.url,
				params: request.params,
			});
		} catch (e) {}
		// console.log(`STARTING ${verb} ${endpoint}`); let start = +new Date;
		const promise = this._getAbortableRequest(request);
		// promise.then(() => console.log(`FINISHED ${verb} ${endpoint} after ${+new Date - start}ms`));
		return promise;
	}

	/**
	 * Given an abortable promise that resolves to a response, return an abortable promise that resolves to data
	 * @param {Promise} abortablePromise
	 * @return {Promise}
	 * @private
	 */
	_getAbortableDataPromise(abortablePromise) {
		const abortableDataPromise = abortablePromise.then(response => response.data);
		abortableDataPromise.abort = abortablePromise.abort;
		return abortableDataPromise;
	}

	/**
	 * Adds the ability to abort requests sent with $http
	 * @param {Object} request  a request object with params for $http
	 */
	_getAbortableRequest(request) {
		request.isPending = true;
		const remove = resp => {
			const idx = this.pendingRequests.indexOf(request);
			if (idx > -1) {
				this.pendingRequests.splice(idx, 1);
			}
			request.isPending = false;
			return resp;
		};
		request.timeout = new Promise((resolve, reject) => {
			request.abort = () => {
				resolve();
				remove();
			};
		});
		this.pendingRequests.push(request);
		const promise = this.$http(request);
		const finalPromise = promise.then(remove, remove);
		finalPromise.abort = () => request.abort();
		return finalPromise;
	}

	/**
	 * Given the endpoint URL, add the appropriate prefix (none, /api, or /api/v2)
	 * @param {String} endpoint  The requested URL fragment such as "/posts/123" or "/v3/posts/search"
	 * @returns {String}
	 * @private
	 */
	_getPrefix(endpoint) {
		endpoint = String(endpoint || '');
		if (endpoint.match(/^(https?)?:\/\//i)) {
			// full url
			return '';
		}
		if (endpoint.match(/\/v\d+\//)) {
			// already has /v2/ or /v3/ at the front
			return '/api';
		}
		// bare url
		return '/api/v2';
	}

	/**
	 * Make a POST request to the specified endpoint and get back the new ID
	 * @param {String} endpoint  The endpoint such as "/posts"
	 * @param {Object} [params]  Data to send in the post body
	 * @param {Object} [headers] Additional headers to send
	 * @returns {Promise}  Promise resolves with the new ID
	 */
	create(endpoint, params, headers) {
		return this.request('POST', endpoint, params, headers).then(response => {
			this.lastCreateResponse = response;
			const isIntString = v => /^[1-9]\d*$/.test(v);
			let newId = response.headers('API-New-Record-Id');
			if (!newId) {
				newId = (response.headers('Location') || '').split('/').filter(isIntString).pop();
			}
			if (isIntString(newId)) {
				return parseInt(newId, 10);
			}
			// another value such as a UUID
			return newId;
		});
	}

	/**
	 * Get data and API-Total-Records header from the specified endpoint
	 * @param {String} endpoint  The endpoint such as "/tags"
	 * @param {Object} [params]  Data to send in the url including limit, page, sort
	 * @param {Object} [headers] Additional headers to send
	 * @returns {Promise}  Promise resolves with an object containing {data, total}
	 */
	getPaginated(endpoint, params, headers) {
		return this.request('GET', endpoint, params, headers).then(response => {
			const total = parseFloat(response.headers('API-Total-Records'));
			const numPages = Math.ceil(total / (params.limit || 25));
			if (total > 0 && numPages < params.page) {
				params.page = 1;
				return this.getPaginated(endpoint, params, headers);
			}
			const isLastPage = params.page == numPages;
			return {
				data: response.data,
				total,
				numPages,
				isLastPage,
			};
		});
	}

	/**
	 * Examine two objects and execute a PATCH request on the differences
	 * @param {String} endpoint  The endpoint such as "/tags/:id"
	 * @param {Object} oldValues  The original values for the record
	 * @param {Object} newValues  The new values for the record
	 * @param {Object} [headers]  Additional headers to send
	 * @returns {Promise}  Promise resolves when PATCH request completes
	 */
	patchDifference(endpoint, oldValues, newValues, headers) {
		const diff = _.reduce(
			newValues,
			(diff, value, prop) => {
				if (!_.isEqual(value, oldValues[prop])) {
					diff[prop] = value;
				}
				return diff;
			},
			{}
		);
		if (_.size(diff) === 0) {
			return Promise.resolve(null);
		}
		return this.request('PATCH', endpoint, diff, headers);
	}
}
